/* Built In Imports */
import NextLink from 'next/link';

/* External Imports */
import {
    Box,
    Link,
    Modal, ModalBody,
    ModalCloseButton, ModalContent,
    ModalHeader, ModalOverlay, useDisclosure
} from '@chakra-ui/react';

/* Internal Imports */
/* Components */
import { refineUrlDomain } from '@components/Utility/utils';
import config from '@config';

/* Services */

/**
 * Returns the Simple Button.
 * @param {Object} style - Style for button.
 * @param {string} bType - Button Type.
 * @param {string} region - Current User Country.
 * @param {string} language - Current User Language.
 * @returns {ReactElement} SimpleButton component.
 */
export default function SimpleButton({
  style,
  bType,
  region,
  lang,
  clickHandler,
  padding,
  textAlign,
  width,
  mb,
  target,
}) {
  const RenderButton = style => {
    const buttonStyle = {
      Orange: (
        <NextLink
          href={refineUrlDomain(style?.linkUrl, region, lang)}
          passHref
          legacyBehavior
        >
          <Link
            mb={mb ? mb : '40px'}
            display="block"
            fontWeight={500}
            fontFamily="FedraSansStd-medium"
            fontSize={'18px'}
            bgColor="#cf4520"
            h={'auto'}
            minH="50px"
            minW="200px"
            textAlign="center"
            padding="14px 29px"
            borderRadius="5px"
            color="#faf7f0"
            textDecoration="none"
            width={width ? width : 'auto'}
            _hover={{
              bg: '#000054',
            }}
            onClick={clickHandler}
            target={
              target != ''
                ? target
                : style?.linkUrl?.indexOf('http') > -1 &&
                  style?.linkUrl.indexOf(config.cdnPath) < 0
                ? '_blank'
                : ''
            }
          >
            {style?.buttonText}
          </Link>
        </NextLink>
      ),
      'White with Orange outline': (
        <NextLink
          href={refineUrlDomain(style?.linkUrl, region, lang)}
          passHref
          legacyBehavior
        >
          <Link
            display="inline-block"
            mb="20px"
            fontFamily="FedraSansStd-medium"
            borderRadius="5px"
            bgColor="transparent"
            border="solid 1px #cf4520"
            color="#cf4520"
            fontSize="18px"
            padding="14px 29px"
            _hover={{
              textDecoration: 'none',
              bgColor: '#cf4520',
              color: 'white',
            }}
            textAlign="center"
            width={'auto'}
            h="auto"
            minH="50px"
            minW="200px"
            m="0 auto"
            onClick={clickHandler}
            target={
              target != ''
                ? target
                : style?.linkUrl?.indexOf('http') > -1 &&
                  style?.linkUrl.indexOf(config.cdnPath) < 0
                ? '_blank'
                : ''
            }
          >
            {style?.buttonText}
          </Link>
        </NextLink>
      ),
      Blue: (
        <NextLink
          href={refineUrlDomain(style?.linkUrl, region, lang)}
          passHref
          legacyBehavior
        >
          <Link
            width="auto"
            minW="200px"
            height="48px"
            borderRadius="4px"
            fontFamily="'FedraSansStd-book', sans-serif"
            fontSize="16px"
            color="#ffffff"
            lineHeight="48px"
            textAlign="center"
            marginTop="30px"
            textDecoration="none"
            colorScheme={'#293B7D'}
            background="#293B7D"
            _hover={{ bg: '#293B7D', border: 'none' }}
            _active={{ bg: '#293B7D', border: 'none' }}
            _focus={{ bg: '#293B7D', border: 'none' }}
          >
            {style?.buttonText}
          </Link>
        </NextLink>
      ),
      Yellow: (
        <NextLink
          href={refineUrlDomain(style?.linkUrl, region, lang)}
          passHref
          legacyBehavior
        >
          <Link
            width="100%"
            minW="200px"
            // maxW="188px"
            height="46px"
            fontFamily="'FedraSansStd-medium', sans-serif"
            fontSize="16px"
            lineHeight="46px"
            background="#EAB646"
            borderRadius="5px"
            color="#28231E"
            fontWeight={'300'}
            textAlign="center"
            marginTop="20px"
            p={5}
            pt="0"
            _hover={{
              background: '#F5E3B5',
              border: '1px solid #F5E3B5',
              color: '#000000',
            }}
            _focus={{
              background: '#F5E3B5',
              border: '1px solid #F5E3B5',
              color: '#000000',
            }}
            _active={{
              background: '#F5E3B5',
              border: '1px solid #F5E3B5',
              color: '#000000',
            }}
            _visited={{
              background: '#F5E3B5',
              border: '1px solid #F5E3B5',
              color: '#000000',
            }}
          >
            {style?.buttonText}
          </Link>
        </NextLink>
      ),
    };
    return buttonStyle[style?.buttonStyle];
  };

  const openModal = style => {
    const { isOpen, onOpen, onClose } = useDisclosure();
    return <>
      <Link
        mb="40px"
        display="block"
        fontWeight={500}
        fontFamily="FedraSansStd-medium"
        fontSize={'18px'}
        bgColor="#cf4520"
        h={'auto'}
        onClick={onOpen}
        textAlign="left"
        padding="10px 36px"
        borderRadius="5px"
        color="faf7f0"
        _hover={{
          textDecoration: 'none',
          bg: '#000054',
        }}
      >
        {style?.buttonText}
      </Link>
      <Modal isOpen={isOpen} onClose={onClose} size={'6xl'}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader
            backgroundColor="#faf7f0"
            borderBottom="1px solid rgb(0 0 0 / 0%)"
            paddingBottom="45px"
            fontFamily="FedraSansStd-A-medium"
            clipPath="polygon(0 0, 100% 0, 100% 100%, 0 75%)"
          >
            <Box as="h4">IMPORTANT NOTE!</Box>
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody
            fontFamily="FedraSansStd-medium"
            fontSize="18px"
            display="flex"
            paddingTop="70px"
          >
            <Box width={{ xl: '20%', md: '20%', lg: '20%', sm: '2%' }}>
              &nbsp;
            </Box>
            <Box width="auto">
              Each patient must sign in with their own email ID. Each ID is
              linked to respective patients and contains all their treatment
              details.
              <Box as="p">&nbsp;</Box>
              In case a patient does not have an email ID, then please create
              a new email ID and sign in.
              <Box as="p">&nbsp;</Box>
              <Box marginTop="20px" textAlign="center">
                <NextLink
                  href={refineUrlDomain(style?.linkUrl)}
                  passHref
                  onClick={onClose}
                  legacyBehavior>
                  <Link
                    display="inline-block"
                    mb="20px"
                    fontFamily="FedraSansStd-medium"
                    borderRadius="5px"
                    bgColor="#cf4520"
                    color="rgb(250 247 240)"
                    fontSize="18px"
                    padding="14px 29px"
                    h="auto"
                    minH="50px"
                    _hover={{
                      textDecoration: 'none',
                    }}
                    target="_blank"
                  >
                    OK
                  </Link>
                </NextLink>
              </Box>
            </Box>
            <Box width={{ xl: '20%', sm: '2%' }}>&nbsp;</Box>
          </ModalBody>

          {/* <ModalFooter>
          <Button colorScheme='blue' mr={3} onClick={onClose}>
            Close
          </Button>
          <Button variant='ghost'>Learn More</Button>
        </ModalFooter> */}
        </ModalContent>
      </Modal>
    </>;
  };

  return (
    <Box
      boxSizing="borderBox"
      display={bType ? 'flex' : 'grid'}
      placeItems={'left'}
      as={'button'}
      color="#faf7f0"
      width={'auto'}
      h="auto"
      minH="50px"
      borderRadius="5px"
      fontSize={'18px'}
      textAlign="center"
      justifyContent={bType ? 'center' : ''}
      cursor="default"
      mb={mb || '50px'}
      mt={'12px'}
    >
      {!bType && RenderButton(style)}
      {bType && openModal(style)}
    </Box>
  );
}
